import api from './client'
import { useAuthStore } from '@/stores/auth'

// /api/files GET ?path= → returns a flat array (handleWebUIFiles)
export interface FileEntry {
  name: string
  size: number
  is_dir: boolean
  mod_time: string // "2006-01-02 15:04" format from backend
  type: string
  /** v3.13.96: true = 不在集群指纹体系里 (手动移入/BT 下载), 仅本机可用 */
  external?: boolean
}

export type FileListResponse = FileEntry[]

export interface FileListEnvelope {
  entries: FileEntry[]
  truncated: boolean
  total: number
}

// v3.19.51: 返回完整信封 — 此前兼容层摊平成纯数组, truncated/total 从未
// 到达调用方 (v3.14.0 的截断提示没落地, 大目录轮询降频也无标记可用)。
export async function listFiles(path = ''): Promise<FileListEnvelope> {
  const raw = await api.get<FileListResponse | FileListEnvelope>('/api/files', { query: { path } })
  if (Array.isArray(raw)) return { entries: raw, truncated: false, total: raw.length }
  if (raw && typeof raw === 'object' && 'entries' in raw) {
    const env = raw as FileListEnvelope
    return {
      entries: env.entries ?? [],
      truncated: !!env.truncated,
      total: env.total ?? env.entries?.length ?? 0,
    }
  }
  return { entries: [], truncated: false, total: 0 }
}

// Download URL helper — used as <a href> directly (auth via cookie/header from page context)
export function downloadFileUrl(name: string): string {
  const url = new URL('/api/files/download', window.location.origin)
  url.searchParams.set('name', name)
  return url.pathname + url.search
}

// v3.13.72: BT 分享 — .torrent 下载 URL (浏览器原生下载, 服务端按需生成+缓存)
export function fileTorrentUrl(name: string): string {
  const url = new URL('/api/files/torrent', window.location.origin)
  url.searchParams.set('name', name)
  return url.pathname + url.search
}

// GET /api/files/magnet?name= → {ok,name,size,is_dir,info_hash,magnet,trackers}
export interface FileMagnetInfo {
  ok: boolean
  name: string
  size: number
  is_dir: boolean
  info_hash: string
  magnet: string
  trackers: string[]
}

// 首次生成大目录的种子需要全量哈希 (可能数十秒), 不走 api client 的默认
// 30s 超时, 单独用 120s AbortController。鉴权走 cookie (公开路径)。
export async function fetchFileMagnet(name: string, timeoutMs = 120_000): Promise<FileMagnetInfo> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)
  try {
    const u = new URL('/api/files/magnet', window.location.origin)
    u.searchParams.set('name', name)
    const resp = await fetch(u.pathname + u.search, {
      credentials: 'include',
      signal: ctrl.signal,
    })
    if (!resp.ok) {
      let message = `HTTP ${resp.status}`
      try {
        const body = await resp.text()
        if (body) message = body
      } catch {
        // keep default message
      }
      throw new Error(message)
    }
    return (await resp.json()) as FileMagnetInfo
  } finally {
    clearTimeout(timer)
  }
}

// /api/files/delete POST {path} — relative to download root
export interface DeleteFileResponse {
  ok: boolean
  deleted: string
}

export async function deleteFile(path: string): Promise<DeleteFileResponse> {
  return api.post<DeleteFileResponse>('/api/files/delete', { path })
}

// v3.13.76: POST /api/files/delete-batch {paths, scope} — 勾选批量删除,
// scope "local" 仅本节点, "cluster" 扇出所有集群节点 (仅上传/仅下载/离线
// 节点自动跳过并在结果中标注)。
export interface NodeDeleteResult {
  id: string
  name: string
  mode?: string
  is_local: boolean
  status: 'deleted' | 'skipped' | 'failed'
  reason?: string
  files: number
  failed?: Record<string, string>
}

export interface DeleteBatchResponse {
  ok: boolean
  scope: string
  nodes: NodeDeleteResult[]
}

export async function deleteFilesBatch(
  paths: string[],
  scope: 'local' | 'cluster',
): Promise<DeleteBatchResponse> {
  return api.post<DeleteBatchResponse>('/api/files/delete-batch', { paths, scope })
}

// /api/files/rename POST {path, new_name} — new_name is basename only
export interface RenameFileResponse {
  ok: boolean
  renamed: string
  to: string
}

export async function renameFile(path: string, newName: string): Promise<RenameFileResponse> {
  return api.post<RenameFileResponse>('/api/files/rename', { path, new_name: newName })
}

// v3.13.96: POST /api/files/adopt {path} — 外部文件转内部 (哈希+注册+广播进
// 集群指纹体系)。全量哈希耗时与磁力生成同级, 不走 api client 默认 30s 超时,
// 单独 120s AbortController (对齐 fetchFileMagnet 模式)。
export interface AdoptFileResponse {
  ok: boolean
  path: string
  is_dir: boolean
  sha256: string // 单文件场景非空; 目录场景为空
  adopted: number // 目录递归采纳成功数 (单文件恒 1)
  failed: number
}

export async function adoptFile(path: string, timeoutMs = 120_000): Promise<AdoptFileResponse> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)
  try {
    // 写操作 — 除 cookie 外再带 Bearer token (对齐 api client 的鉴权附加,
    // token-only 部署下 cookie 缺失也能通过)。
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const token = useAuthStore().getToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
    const resp = await fetch('/api/files/adopt', {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ path }),
      signal: ctrl.signal,
    })
    if (!resp.ok) {
      let message = `HTTP ${resp.status}`
      try {
        const body = await resp.text()
        if (body) message = body
      } catch {
        // keep default message
      }
      throw new Error(message)
    }
    return (await resp.json()) as AdoptFileResponse
  } finally {
    clearTimeout(timer)
  }
}

export interface UploadProgress {
  loaded: number
  total: number
  percent: number
}

// Backend response shape for POST /api/files/upload (multipart). The actual
// fields are forward-looking; the UI tolerates missing fields by defaulting.
export interface UploadResponse {
  ok: boolean
  name: string
  path: string
  size: number
}

export interface UploadOptions {
  /** Existing behaviour flags (mapped to addUri options when set). */
  allowOverwrite?: boolean
  autoRename?: boolean
}

// Upload a single file via multipart/form-data to POST /api/files/upload.
//
// Auth: page must already have an active session — the XHR sends cookies
// (`withCredentials = true`). The Authorization header is also attached when
// present, so token-based auth still works.
//
// Progress: fetch() doesn't expose upload progress, so we use XHR and forward
// `xhr.upload.onprogress` to the optional callback.
//
// Content-Type: the browser sets the multipart boundary automatically — do
// NOT set `Content-Type` on the XHR, otherwise the boundary is lost and the
// server sees an empty body.
export function uploadFile(
  file: File,
  destPath?: string,
  options: UploadOptions = {},
  onProgress?: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<UploadResponse> {
  return new Promise<UploadResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const url = new URL('/api/files/upload', window.location.origin)
    if (destPath) url.searchParams.set('path', destPath)
    if (options.allowOverwrite !== undefined) {
      url.searchParams.set('allow-overwrite', options.allowOverwrite ? 'true' : 'false')
    }
    if (options.autoRename !== undefined) {
      url.searchParams.set('auto-file-renaming', options.autoRename ? 'true' : 'false')
    }

    xhr.open('POST', url.pathname + url.search, true)
    xhr.withCredentials = true
    xhr.setRequestHeader('Accept', 'application/json')

    if (signal) {
      if (signal.aborted) {
        xhr.abort()
      } else {
        signal.addEventListener('abort', () => xhr.abort(), { once: true })
      }
    }

    xhr.upload.onprogress = (e: ProgressEvent) => {
      if (e.lengthComputable && onProgress) {
        onProgress({
          loaded: e.loaded,
          total: e.total,
          percent: Math.round((e.loaded / e.total) * 100),
        })
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Try to parse JSON; fall back to a minimal success envelope.
        let parsed: Partial<UploadResponse> = {}
        const ct = xhr.getResponseHeader('Content-Type') ?? ''
        if (ct.includes('application/json') && xhr.responseText) {
          try {
            parsed = JSON.parse(xhr.responseText) as Partial<UploadResponse>
          } catch {
            parsed = {}
          }
        }
        resolve({
          ok: parsed.ok ?? true,
          name: parsed.name ?? file.name,
          path: parsed.path ?? destPath ?? '',
          size: parsed.size ?? file.size,
        })
      } else {
        let message = `Upload failed (${xhr.status})`
        if (xhr.responseText) {
          try {
            const body = JSON.parse(xhr.responseText) as { message?: unknown }
            if (body && typeof body.message === 'string') message = body.message
          } catch {
            // ignore — keep default message
          }
        }
        const err = new Error(message) as Error & { status: number }
        err.status = xhr.status
        reject(err)
      }
    }
    xhr.onerror = () => {
      // Aborted requests also surface as onerror in some browsers — let
      // the abort path handle them via the signal listener.
      if (signal?.aborted) {
        reject(new DOMException('Upload aborted', 'AbortError'))
        return
      }
      reject(new Error('Network error during upload'))
    }
    xhr.onabort = () => {
      if (signal?.aborted) {
        reject(new DOMException('Upload aborted', 'AbortError'))
      } else {
        reject(new Error('Upload aborted'))
      }
    }

    const form = new FormData()
    form.append('file', file)
    xhr.send(form)
  })
}
